import axios from "axios";

import { API } from "../backend";
import ContractUploader from "../contractimport/components/ContractUploader";
import "./contractimport.css";
import AddContractService from "../services/contract/addContractService";
import AddContractLoanService from "../services/contract/addContractLoanService";
import AddPartyService from "../services/party/addparty.service";
import AddPartyAddressService from "../services/party/address/addAddress.service";
import AddAssetService from "../services/contract/asset/addAssetService";
import AddPaymentScheduleService from "../services/contract/paymentSchedule/addPaymentScheduleService";
import authHeader from "../services/auth-header";

export default function ContractImportPage() {
  const PARTY_ROLE_MAP = {
    borrower: "Customer",
    customer: "Customer",
    co_obligor: "Customer",
    lender: "Vendor",
    vendor: "Vendor",
    guarantor: "Guarantor",
    insurer: "Insurer",
  };

  const normalizeText = (value) => {
    if (value === undefined || value === null) {
      return "";
    }
    return String(value).trim();
  };

  const getErrorMessage = (error, fallback = "Something went wrong.") => {
    const responseData = error?.response?.data;

    if (typeof responseData === "string" && responseData.trim()) {
      return responseData;
    }
    if (typeof responseData?.error === "string" && responseData.error.trim()) {
      return responseData.error;
    }
    if (typeof responseData?.errors === "string" && responseData.errors.trim()) {
      return responseData.errors;
    }
    if (typeof responseData?.detail === "string" && responseData.detail.trim()) {
      return responseData.detail;
    }
    if (responseData && typeof responseData === "object") {
      const fieldMessages = Object.entries(responseData)
        .map(([field, value]) => {
          if (Array.isArray(value)) {
            return `${field}: ${value.map((item) => item?.string || item).join(", ")}`;
          }
          if (typeof value === "string") {
            return `${field}: ${value}`;
          }
          return "";
        })
        .filter(Boolean);

      if (fieldMessages.length > 0) {
        return fieldMessages.join(" | ");
      }
    }
    if (typeof error?.message === "string" && error.message.trim()) {
      return error.message;
    }
    return fallback;
  };

  const toNumber = (value, fallback = 0) => {
    const normalized = normalizeText(value).replace(/,/g, "");
    if (!normalized) {
      return fallback;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const toNullableNumber = (value) => {
    const normalized = normalizeText(value).replace(/,/g, "");
    if (!normalized) {
      return null;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const toDateString = (value) => {
    const normalized = normalizeText(value);
    return normalized || null;
  };

  const parseDate = (value) => {
    const normalized = toDateString(value);
    if (!normalized) {
      return null;
    }

    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const formatDate = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      return null;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const frequencyMap = {
    monthly: "Monthly",
    quarterly: "Quarterly",
    bi_annual: "Semi-annual",
    "bi-annual": "Semi-annual",
    semi_annual: "Semi-annual",
    annually: "Annual",
    annual: "Annual",
  };

  const normalizeFrequency = (value) => {
    const normalized = normalizeText(value).toLowerCase();
    return frequencyMap[normalized] || "Monthly";
  };

  const getFrequencyDivisor = (frequency) => {
    const mapping = {
      Monthly: 1,
      Quarterly: 3,
      "Semi-annual": 6,
      Annual: 12,
    };

    return mapping[frequency] || 1;
  };

  const getFrequencyMonths = (frequency) => {
    return getFrequencyDivisor(frequency);
  };

  const calculateEndDate = ({ startDate, contractTerm, frequency }) => {
    const parsedStartDate = parseDate(startDate);
    if (!parsedStartDate || !contractTerm) {
      return null;
    }

    const monthsToAdd = Math.max(1, Number(contractTerm)) || 1;
    const calculatedDate = new Date(parsedStartDate);
    calculatedDate.setMonth(calculatedDate.getMonth() + monthsToAdd);
    calculatedDate.setDate(calculatedDate.getDate() - 1);

    return formatDate(calculatedDate);
  };

  const calculatePaymentStartDate = ({ startDate, frequency, paymentRule = "Advance" }) => {
    const parsedStartDate = parseDate(startDate);
    if (!parsedStartDate) {
      return null;
    }

    if (paymentRule === "Advance") {
      return formatDate(parsedStartDate);
    }

    const monthsToAdd = getFrequencyMonths(frequency);
    const calculatedDate = new Date(parsedStartDate);
    calculatedDate.setMonth(calculatedDate.getMonth() + monthsToAdd);
    calculatedDate.setDate(calculatedDate.getDate() - 1);

    return formatDate(calculatedDate);
  };

  const normalizeContractType = (value) => {
    const normalized = normalizeText(value).toLowerCase();

    if (normalized.includes("loan")) {
      return "Loan";
    }
    if (normalized.includes("rental")) {
      return "Rental";
    }
    return "Lease";
  };

  const buildContractTerms = (contract = {}) => {
    const fields = [
      ["Governing Law", contract.governing_law],
      ["Late Fee Percent", contract.late_fee_percent],
      ["Late Fee Grace Days", contract.late_fee_grace_days],
      ["Prepayment Penalty", contract.prepayment_penalty],
      ["Fees", contract.fees],
      ["DocuSign Envelope ID", contract.docusign_envelope_id],
      ["Account Number", contract.account_number],
      ["External Reference", contract.external_reference],
      ["Currency", contract.currency],
      ["Rate Type", contract.rate_type],
    ];

    return fields
      .filter(([, value]) => normalizeText(value))
      .map(([name, value]) => ({
        name,
        value: String(value),
      }));
  };

  const getPartyTypeFromRole = (role) => {
    const normalizedRole = normalizeText(role).toLowerCase();
    return PARTY_ROLE_MAP[normalizedRole] || "Customer";
  };

  const getCustomerType = (entityType) => {
    const normalizedEntityType = normalizeText(entityType).toLowerCase();
    return normalizedEntityType === "individual" ? "Person" : "Organization";
  };

  const getResponseItems = (responseData) => {
    if (Array.isArray(responseData)) {
      return responseData;
    }
    if (Array.isArray(responseData?.data)) {
      return responseData.data;
    }
    if (Array.isArray(responseData?.results)) {
      return responseData.results;
    }
    return [];
  };

  const findExistingParty = async (partyType, legalName) => {
    const customerName = normalizeText(legalName);
    if (!customerName) {
      return null;
    }

    const response = await axios.get(
      `${API}parties/vendor/?party_type=${encodeURIComponent(
        partyType
      )}&&customer_name=${encodeURIComponent(customerName)}`,
      { headers: authHeader() }
    );

    const matches = getResponseItems(response.data);
    return (
      matches.find(
        (item) =>
          normalizeText(item.customer_name).toLowerCase() ===
          customerName.toLowerCase()
      ) || matches[0] || null
    );
  };

  const ensureParty = async (party) => {
    const legalName = normalizeText(party?.legal_name);
    if (!legalName) {
      return null;
    }

    const partyType = getPartyTypeFromRole(party?.role);
    const existingParty = await findExistingParty(partyType, legalName);
    if (existingParty?.id) {
      return existingParty.id;
    }

    let createResponse;

    try {
      createResponse = await AddPartyService.addParty(
        getCustomerType(party?.entity_type),
        partyType,
        legalName,
        null,
        "",
        "",
        "",
        "",
        normalizeText(party?.primary_contact),
        "",
        "",
        "",
        "",
        "",
        null,
        [],
        "",
        null,
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        false
      );
    } catch (error) {
      throw new Error(getErrorMessage(error, `Unable to create ${partyType}.`));
    }

    if (!createResponse?.data?.success || !createResponse?.data?.partyId) {
      throw new Error(createResponse?.data?.error || `Unable to create ${partyType}.`);
    }

    return createResponse.data.partyId;
  };

  const ensureAddress = async (partyId, address, options = {}) => {
    const street = normalizeText(address?.street) || "Imported address";
    const city = normalizeText(address?.city) || "Unknown";
    const state = normalizeText(address?.state) || "NA";
    const zip = normalizeText(address?.zip) || "00000";
    const country = normalizeText(address?.country) || "Unknown";

    let response;

    try {
      response = await AddPartyAddressService.addPartyAddress(
        partyId,
        street,
        "",
        city,
        state,
        zip,
        country,
        options.billTo || "Yes",
        options.shipTo || "No",
        options.installTo || "Yes",
        []
      );
    } catch (error) {
      throw new Error(getErrorMessage(error, "Unable to create address."));
    }

    if (!response?.data?.success || !response?.data?.addressId) {
      throw new Error(response?.data?.error || "Unable to create address.");
    }

    return response.data.addressId;
  };

  const createAssets = async ({
    contractId,
    assets,
    customerAddressId,
    vendorPartyId,
    contractType,
    contractDownPayment,
  }) => {
    for (let index = 0; index < assets.length; index += 1) {
      const asset = assets[index];
      const quantity = Math.max(1, toNumber(asset.quantity, 1));
      const unitCost = toNumber(asset.unit_cost, 0);
      const deliveryCost = toNumber(asset.delivery_cost, 0);
      const financedAmount = unitCost * quantity;
      const totalCost = financedAmount + deliveryCost;

      let assetResponse;

      try {
        assetResponse = await AddAssetService.addAsset(
          contractId,
          "",
          normalizeText(asset.description) || `Imported Asset ${index + 1}`,
          "",
          financedAmount,
          "",
          normalizeText(asset.serial_number),
          "",
          deliveryCost,
          normalizeText(asset.make),
          normalizeText(asset.make),
          normalizeText(asset.model),
          "",
          0,
          customerAddressId,
          vendorPartyId,
          null,
          null,
          0,
          unitCost,
          "Dealer",
          0,
          "Dealer",
          0,
          "Borrower",
          0,
          "Dealer",
          totalCost,
          contractDownPayment,
          "Borrower",
          normalizeText(asset.asset_class) || contractType,
          [],
          "",
          "",
          normalizeText(asset.unit_number),
          ""
        );
      } catch (error) {
        throw new Error(getErrorMessage(error, "Unable to create asset."));
      }

      if (!assetResponse?.data?.success) {
        throw new Error(
          assetResponse?.data?.error ||
            assetResponse?.data?.errors ||
            "Unable to create asset."
        );
      }
    }
  };

  const getContractNumberMode = async () => {
    try {
      const response = await axios.get(`${API}contracts/contract_number_method/`, {
        headers: authHeader(),
      });

      if (response?.data?.success === 1) {
        return normalizeText(response.data.details);
      }
    } catch (error) {
      return "";
    }

    return "";
  };

  const createPaymentSchedule = async ({
    contractId,
    contractTerm,
    paymentAmount,
    frequency,
    startDate,
    endDate,
    paymentStartDate,
  }) => {
    if (!paymentAmount || !contractTerm || !startDate || !endDate) {
      return;
    }

    const divisor = getFrequencyDivisor(frequency);
    const noOfPayments = Math.max(1, Math.round(contractTerm / divisor));
    const paymentRows = [
      {
        no_of_payments: noOfPayments,
        payment_type: "Rent",
        amount: paymentAmount,
        payment_frequency: frequency === "Semi-annual" ? "Semi-annually" : frequency,
        start_date: startDate,
        end_date: endDate,
        paymentstart_date: paymentStartDate || startDate,
      },
    ];

    try {
      const response = await AddPaymentScheduleService.addPaymentSchedule(
        contractId,
        noOfPayments,
        "Rent",
        paymentAmount,
        frequency === "Semi-annual" ? "Semi-annually" : frequency,
        startDate,
        endDate,
        paymentRows
      );

      if (!response?.data?.success) {
        throw new Error(response?.data?.error || "Unable to create payment schedule.");
      }
    } catch (error) {
      throw new Error(getErrorMessage(error, "Unable to create payment schedule."));
    }
  };

  const handleFinanced = async (contractId) => {
    try {
      await axios.get(`${API}contracts/financedamount/${contractId}/`, {
        headers: authHeader(),
      });
    } catch (error) {
      throw new Error(getErrorMessage(error, "Unable to calculate financed amount."));
    }
  };

  const handleLoanPayment = async (contractId) => {
    try {
      await axios.get(`${API}contracts/paymentschedule-view/${contractId}/`, {
        headers: authHeader(),
      });
    } catch (error) {
      throw new Error(getErrorMessage(error, "Unable to load loan payment schedule."));
    }
  };

  const handleIir = async (contractId) => {
    try {
      await axios.get(`${API}contracts/contractiir/?id=${contractId}`, {
        headers: authHeader(),
      });
    } catch (error) {
      throw new Error(getErrorMessage(error, "Unable to calculate IIR."));
    }
  };

  const handleImportReady = async (extractedData) => {
    const parties = extractedData?.parties || [];
    const contract = extractedData?.contract || {};
    const assets = extractedData?.assets || [];

    const contractType = normalizeContractType(contract.structure);
    const customerParty =
      parties.find(
        (party) => getPartyTypeFromRole(party?.role) === "Customer"
      ) || parties[0];
    const vendorParty =
      parties.find((party) => getPartyTypeFromRole(party?.role) === "Vendor") ||
      customerParty;

    if (!customerParty) {
      throw new Error("Customer party information is missing.");
    }

    const customerPartyId = await ensureParty(customerParty);
    const vendorPartyId = vendorParty
      ? await ensureParty(vendorParty)
      : customerPartyId;

    const customerAddressId = await ensureAddress(customerPartyId, customerParty.address, {
      billTo: "Yes",
      shipTo: "No",
      installTo: "Yes",
    });

    if (vendorPartyId && vendorParty?.address) {
      await ensureAddress(vendorPartyId, vendorParty.address, {
        billTo: "Yes",
        shipTo: "No",
        installTo: "No",
      });
    }

    const contractTerms = buildContractTerms(contract);
    const startDate =
      toDateString(contract.execution_date) ||
      toDateString(contract.first_payment_date);
    const contractTerm = toNumber(contract.term_months, 0);
    const frequency = normalizeFrequency(contract.payment_frequency);
    const paymentRule = contractType === "Loan" ? "Arrears" : "Advance";
    const paymentStartDate =
      toDateString(contract.first_payment_date) ||
      calculatePaymentStartDate({
        startDate,
        frequency,
        paymentRule,
      }) ||
      startDate;
    const endDate =
      toDateString(contract.last_payment_date) ||
      calculateEndDate({
        startDate,
        contractTerm,
        frequency,
      });
    const extractedContractNumber = normalizeText(contract.contract_number);
    const depositAmount = toNumber(contract.down_payment, 0);
    const contractNumberMode = await getContractNumberMode();
    const contractNumber =
      contractNumberMode === "Manual" && !extractedContractNumber
        ? ""
        : extractedContractNumber;

    if (contractNumberMode === "Manual" && !contractNumber) {
      throw new Error("Contract number is required because your company uses manual contract numbering.");
    }

    if (!startDate) {
      throw new Error("Contract start date could not be determined from the imported document.");
    }

    if (!endDate) {
      throw new Error("Contract end date could not be determined from the imported document.");
    }

    let contractResponse;

    if (contractType === "Loan") {
      try {
        contractResponse = await AddContractLoanService.addContract(
          contractNumber,
          customerPartyId,
          startDate,
          contractTerm,
          customerAddressId,
          "Loan",
          endDate,
          paymentStartDate,
          toNumber(contract.amount_financed, 0),
          toNumber(contract.interest_rate, 0),
          "Declining Balance",
          "Equal Monthly Installment",
          "30/360",
          paymentRule,
          depositAmount,
          0,
          0,
          0,
          [],
          frequency === "Semi-annual" ? "Semi-annually" : frequency,
          paymentRule,
          false,
          "",
          "",
          "",
          "",
          "",
          "",
          0,
          contractTerms,
          normalizeText(contract.rate_type).toLowerCase() === "variable"
            ? "Floating"
            : "Fixed",
          0,
          0
        );
      } catch (error) {
        throw new Error(getErrorMessage(error, "Unable to create contract."));
      }
    } else {
      const normalizedBackendFrequency =
        frequency === "Semi-annual" ? "Semi-annually" : frequency;

      try {
        contractResponse = await AddContractService.addContract(
          contractNumber,
          customerPartyId,
          startDate,
          contractTerm,
          customerAddressId,
          contractType,
          endDate,
          depositAmount,
          0,
          0,
          0,
          paymentRule,
          [],
          paymentStartDate,
          toNullableNumber(contract.payment_amount),
          normalizedBackendFrequency,
          "Rent",
          false,
          "",
          "",
          "",
          "",
          "",
          "",
          contractTerms
        );
      } catch (error) {
        throw new Error(getErrorMessage(error, "Unable to create contract."));
      }
    }

    const createdContractId =
      contractResponse?.data?.contractID || contractResponse?.data?.id;

    if (!contractResponse?.data?.success || !createdContractId) {
      throw new Error(contractResponse?.data?.error || "Unable to create contract.");
    }

    if (assets.length > 0) {
      await createAssets({
        contractId: createdContractId,
        assets,
        customerAddressId,
        vendorPartyId,
        contractType,
        contractDownPayment: depositAmount,
      });
    }

    if (contractType === "Loan") {
      await handleFinanced(createdContractId);
      await handleLoanPayment(createdContractId);
    } else {
      await createPaymentSchedule({
        contractId: createdContractId,
        contractTerm,
        paymentAmount: toNullableNumber(contract.payment_amount),
        frequency,
        startDate,
        endDate,
        paymentStartDate,
      });
      await handleFinanced(createdContractId);
      await handleIir(createdContractId);
    }

    return {
      ...contractResponse.data,
      contractID: createdContractId,
    };
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <ContractUploader
        // apiUrl="http://localhost:8000/extract"
        apiUrl={API+ "extract/extract_contract/"}
        onImportReady={handleImportReady}
      />
    </div>
  );
}
